import type {
  HydratePayload,
  IState,
  ImportOption,
  ImportRow,
} from "../stores/file-to-storydoc-generation.store";

// The backend keeps a job for 48h; stop resuming a little before that.
export const IMPORT_SESSION_TTL_MS = 47 * 60 * 60 * 1000;
const VERSION = 1;

const key = (userId: string) => `file-import:${userId}`;

const LIST_VERSION = 2;
const listKey = (userId: string) => `file-import:v2:${userId}`;

type PersistedRow = Omit<
  ImportRow,
  "options" | "sourceSlide" | "undo" | "restoredK" | "undoing"
> & {
  optionTitles: Array<Pick<ImportOption, "k" | "title">> | null;
  // absent on entries saved before it existed
  selectedK?: number | null;
  undo: { setRef: ImportRow["setRef"]; selected: number } | null;
};

type Persisted = Omit<HydratePayload, "rows"> & {
  version: number;
  rows: PersistedRow[];
};

type StoredList = { version: number; sessions: Persisted[] };

const toPersistedRow = ({
  options,
  sourceSlide: _s,
  undo,
  restoredK,
  undoing: _u,
  ...row
}: ImportRow): PersistedRow => ({
  ...row,
  optionTitles: options?.map(({ k, title }) => ({ k, title })) ?? null,
  selectedK: options?.[row.selected]?.k ?? restoredK ?? null,
  undo: undo ? { setRef: undo.setRef, selected: undo.selected } : null,
});

const fromPersistedRow = ({
  optionTitles: _t,
  selectedK,
  undo,
  ...row
}: PersistedRow): ImportRow => ({
  ...row,
  restoredK: selectedK ?? null,
  options: null,
  sourceSlide: null,
  undoing: false,
  undo: undo ? { ...undo, options: null } : null,
});

const toPersisted = (state: IState): Persisted | null => {
  if (!state.jobId || !state.startedAt) return null;
  const {
    startedJob: _j,
    modalOpen: _m,
    completeToastDismissed: _d,
    progressToastDismissed: _p,
    createdStory: _c,
    errorMsg: _e,
    target: _t,
    keepDeckTheme: _k,
    themeOnly: _o,
    upload: _u,
    rows,
    ...rest
  } = state;
  return { ...rest, version: VERSION, rows: rows.map(toPersistedRow) };
};

const fromPersisted = (
  parsed: Persisted,
  now: number,
): HydratePayload | null => {
  if (
    !parsed ||
    !parsed.jobId ||
    !parsed.startedAt ||
    !Array.isArray(parsed.rows)
  )
    return null;
  if (now - new Date(parsed.startedAt).getTime() > IMPORT_SESSION_TTL_MS)
    return null;
  const { version: _v, rows, ...rest } = parsed;
  try {
    return { ...rest, rows: rows.map(fromPersistedRow) };
  } catch {
    return null;
  }
};

const clearImportSession = (userId: string) => {
  try {
    localStorage.removeItem(key(userId));
  } catch {}
};

const loadImportSession = (
  userId: string,
  now = Date.now(),
): HydratePayload | null => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(key(userId));
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: Persisted;
  try {
    parsed = JSON.parse(raw) as Persisted;
  } catch {
    clearImportSession(userId);
    return null;
  }
  if (parsed?.version !== VERSION) {
    clearImportSession(userId);
    return null;
  }
  const payload = fromPersisted(parsed, now);
  if (!payload) clearImportSession(userId);
  return payload;
};

export const saveImportSessions = (userId: string, states: IState[]) => {
  const sessions = states
    .map(toPersisted)
    .filter((p): p is Persisted => p != null);
  const write = (list: Persisted[]) =>
    localStorage.setItem(
      listKey(userId),
      JSON.stringify({
        version: LIST_VERSION,
        sessions: list,
      } satisfies StoredList),
    );
  const remove = () => {
    try {
      localStorage.removeItem(listKey(userId));
    } catch {}
  };
  if (sessions.length === 0) return remove();
  try {
    return write(sessions);
  } catch {}
  // quota or private mode: the jobs still run; resume is best-effort
  try {
    write(
      sessions.map((p) => ({
        ...p,
        rows: p.rows.map((r) => ({ ...r, thumbnail: "" })),
      })),
    );
  } catch {
    remove();
  }
};

export const clearImportSessions = (userId: string) => {
  try {
    localStorage.removeItem(listKey(userId));
    localStorage.removeItem(key(userId));
  } catch {}
};

export const loadImportSessions = (
  userId: string,
  now = Date.now(),
): HydratePayload[] => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(listKey(userId));
  } catch {
    return [];
  }
  if (!raw) {
    const legacy = loadImportSession(userId, now);
    clearImportSession(userId);
    if (!legacy) return [];
    try {
      localStorage.setItem(
        listKey(userId),
        JSON.stringify({
          version: LIST_VERSION,
          sessions: [
            {
              ...legacy,
              version: VERSION,
              rows: legacy.rows.map(toPersistedRow),
            },
          ],
        } satisfies StoredList),
      );
    } catch {}
    return [legacy];
  }
  let parsed: StoredList;
  try {
    parsed = JSON.parse(raw) as StoredList;
  } catch {
    clearImportSessions(userId);
    return [];
  }
  if (parsed?.version !== LIST_VERSION || !Array.isArray(parsed.sessions)) {
    clearImportSessions(userId);
    return [];
  }
  return parsed.sessions
    .map((p) => fromPersisted(p, now))
    .filter((p): p is HydratePayload => p != null);
};
